import { useMemo, useCallback } from "react";
import {
  Modal,
  Stack,
  Group,
  Text,
  Button,
  Table,
  Badge,
  Divider,
  Box,
  Paper,
  Loader,
} from "@mantine/core";
import {
  IconFileReport,
  IconAlertTriangle,
  IconFileTypePdf,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import { useOverdueSendOuts } from "../../hooks/useSendOuts";
import { SendOut } from "../../services/sendOuts.service";
import {
  formatExpectedReturnDate,
  formatLabCurrentDate,
  formatLabCurrentDateTime,
  formatSendOutInstantDate,
} from "./sendOutDatePresentation";

interface OverdueReportModalProps {
  opened: boolean;
  onClose: () => void;
}

interface DestinationGroup {
  name: string;
  sendOuts: SendOut[];
  ages: number[];
}

export default function OverdueReportModal({
  opened,
  onClose,
}: OverdueReportModalProps) {
  const { data: overdueSendOuts, isLoading } = useOverdueSendOuts();

  // Group by destination
  const grouped = useMemo(() => {
    if (!overdueSendOuts) return [];

    const map = new Map<string, DestinationGroup>();

    overdueSendOuts.forEach((so) => {
      const name = so.externalInstitution?.name || "Unknown";
      if (!map.has(name)) {
        map.set(name, { name, sendOuts: [], ages: [] });
      }
      const group = map.get(name)!;
      group.sendOuts.push(so);
      const ageDays = so.shippedAt
        ? dayjs().diff(dayjs(so.shippedAt), "day")
        : 0;
      group.ages.push(ageDays);
    });

    return Array.from(map.values()).sort((a, b) => {
      // Sort by worst overdue first
      const maxA = Math.max(...a.ages);
      const maxB = Math.max(...b.ages);
      return maxB - maxA;
    });
  }, [overdueSendOuts]);

  const handleExportPDF = useCallback(() => {
    if (!overdueSendOuts || overdueSendOuts.length === 0) return;

    const summaryRows = grouped
      .map(
        (g) =>
          `<tr>
            <td>${g.name}</td>
            <td style="text-align:center">${g.sendOuts.length}</td>
            <td style="text-align:center">${Math.max(...g.ages)}</td>
          </tr>`
      )
      .join("");

    const detailRows = overdueSendOuts
      .map((so) => {
        const ageDays = so.shippedAt
          ? dayjs().diff(dayjs(so.shippedAt), "day")
          : 0;

        // Use resolved labels if available, otherwise fallback to counts
        // For partially received items, we only care about outstanding materials
        const slideLabels = so.slideLabels || [];
        const blockLabels = so.blockLabels || [];

        const slideCount =
          slideLabels.length ||
          (so.slidesSent?.length || 0) - (so.slidesReturned?.length || 0);
        const blockCount =
          blockLabels.length ||
          (so.blocksSent?.length || 0) - (so.blocksReturned?.length || 0);

        const materials: string[] = [];
        if (slideCount > 0) {
          const labelPart =
            slideLabels.length > 0 ? ` (${slideLabels.join(", ")})` : "";
          materials.push(
            `${slideCount} slide${slideCount > 1 ? "s" : ""}${labelPart}`
          );
        }
        if (blockCount > 0) {
          const labelPart =
            blockLabels.length > 0 ? ` (${blockLabels.join(", ")})` : "";
          materials.push(
            `${blockCount} block${blockCount > 1 ? "s" : ""}${labelPart}`
          );
        }

        const lastFollowUp =
          so.followUps && so.followUps.length > 0
            ? so.followUps.reduce((latest, fu) =>
                new Date(fu.createdAt) > new Date(latest.createdAt)
                  ? fu
                  : latest
              )
            : null;

        return `<tr>
          <td>${so.case?.caseNumber || "N/A"}</td>
          <td>${so.externalInstitution?.name || "N/A"}</td>
          <td class="overdue">${ageDays} days</td>
          <td>${
            so.shippedAt ? formatSendOutInstantDate(so.shippedAt) : "N/A"
          }</td>
          <td>${
            so.expectedReturnDate
              ? formatExpectedReturnDate(so.expectedReturnDate)
              : "N/A"
          }</td>
          <td>${materials.length > 0 ? materials.join(", ") : "N/A"}</td>
          <td>${
            lastFollowUp
              ? formatSendOutInstantDate(lastFollowUp.createdAt)
              : "None"
          }</td>
        </tr>`;
      })
      .join("");

    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    printWindow.document.write(`<!DOCTYPE html>
<html><head>
  <title>Overdue Send-Outs Report</title>
  <style>
    body { font-family: Arial, Helvetica, sans-serif; padding: 24px; color: #222; }
    h1 { font-size: 20px; margin: 0 0 4px; }
    .meta { color: #666; font-size: 13px; margin-bottom: 20px; }
    .alert-box { background: #fff5f5; border: 1px solid #e8c4c4; border-radius: 6px; padding: 12px 16px; margin-bottom: 24px; }
    .alert-box strong { color: #c92a2a; }
    h2 { font-size: 15px; margin: 20px 0 8px; border-bottom: 1px solid #ddd; padding-bottom: 4px; }
    table { width: 100%; border-collapse: collapse; font-size: 13px; margin-bottom: 16px; }
    th { background: #f4f4f4; text-align: left; padding: 6px 8px; border: 1px solid #ddd; font-weight: 600; }
    td { padding: 6px 8px; border: 1px solid #ddd; }
    .overdue { color: #c92a2a; font-weight: 600; }
    @media print {
      body { padding: 0.75in; }
      @page { margin: 0; }
    }
  </style>
</head><body>
  <h1>Overdue Send-Outs Report</h1>
  <p class="meta">Generated: ${formatLabCurrentDateTime()}</p>
  <div class="alert-box">
    <strong>${overdueSendOuts.length} overdue send-out${
      overdueSendOuts.length !== 1 ? "s" : ""
    }</strong>
    across ${grouped.length} destination${grouped.length !== 1 ? "s" : ""}
  </div>

  <h2>Summary by Destination</h2>
  <table>
    <thead><tr><th>Destination</th><th style="text-align:center">Count</th><th style="text-align:center">Max Days</th></tr></thead>
    <tbody>${summaryRows}</tbody>
  </table>

  <h2>Details</h2>
  <table>
    <thead><tr>
      <th>Case #</th><th>Destination</th><th>Days Overdue</th>
      <th>Shipped</th><th>Expected</th><th>Materials</th><th>Last Follow-Up</th>
    </tr></thead>
    <tbody>${detailRows}</tbody>
  </table>
</body></html>`);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  }, [overdueSendOuts, grouped]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group>
          <IconFileReport size={20} />
          <Text fw={600}>Overdue Send-Outs Report</Text>
        </Group>
      }
      size="xl"
      centered
      styles={{
        body: {
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
        },
      }}
    >
      {isLoading ? (
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      ) : !overdueSendOuts || overdueSendOuts.length === 0 ? (
        <Stack align="center" py="xl">
          <Text c="dimmed">No overdue send-outs</Text>
        </Stack>
      ) : (
        <Stack gap="md" p={4}>
          {/* Summary Header */}
          <Paper withBorder p="md" bg="red.0" radius="md">
            <Group justify="space-between">
              <Group>
                <IconAlertTriangle
                  size={24}
                  color="var(--mantine-color-red-6)"
                />
                <Box>
                  <Text fw={600} size="lg">
                    {overdueSendOuts.length} Overdue Send-Out
                    {overdueSendOuts.length !== 1 ? "s" : ""}
                  </Text>
                  <Text size="sm" c="dimmed">
                    {formatLabCurrentDate()}
                  </Text>
                </Box>
              </Group>
            </Group>
          </Paper>

          {/* By Destination */}
          <Box>
            <Text fw={600} mb="sm">
              By Destination
            </Text>
            <Stack gap="xs">
              {grouped.map((group) => (
                <Group key={group.name} justify="space-between">
                  <Text size="sm" fw={500}>
                    {group.name}
                  </Text>
                  <Group gap="xs">
                    <Badge color="red" variant="light">
                      {group.sendOuts.length}
                    </Badge>
                    <Text size="xs" c="dimmed">
                      ({group.ages.join(", ")} days)
                    </Text>
                  </Group>
                </Group>
              ))}
            </Stack>
          </Box>

          <Divider />

          {/* Detail Table */}
          <Box>
            <Text fw={600} mb="sm">
              Details
            </Text>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Case #</Table.Th>
                  <Table.Th>Destination</Table.Th>
                  <Table.Th>Days Overdue</Table.Th>
                  <Table.Th>Shipped</Table.Th>
                  <Table.Th>Expected</Table.Th>
                  <Table.Th>Materials</Table.Th>
                  <Table.Th>Last Follow-Up</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {overdueSendOuts.map((so) => {
                  const ageDays = so.shippedAt
                    ? dayjs().diff(dayjs(so.shippedAt), "day")
                    : 0;

                  // Use resolved labels if available, otherwise fallback to counts
                  // For partially received items, we only care about outstanding materials
                  const slideLabels = so.slideLabels || [];
                  const blockLabels = so.blockLabels || [];

                  const slideCount =
                    slideLabels.length ||
                    (so.slidesSent?.length || 0) -
                      (so.slidesReturned?.length || 0);
                  const blockCount =
                    blockLabels.length ||
                    (so.blocksSent?.length || 0) -
                      (so.blocksReturned?.length || 0);

                  const materials: string[] = [];
                  if (slideCount > 0) {
                    const labelPart =
                      slideLabels.length > 0
                        ? ` (${slideLabels.join(", ")})`
                        : "";
                    materials.push(
                      `${slideCount} slide${
                        slideCount > 1 ? "s" : ""
                      }${labelPart}`
                    );
                  }
                  if (blockCount > 0) {
                    const labelPart =
                      blockLabels.length > 0
                        ? ` (${blockLabels.join(", ")})`
                        : "";
                    materials.push(
                      `${blockCount} block${
                        blockCount > 1 ? "s" : ""
                      }${labelPart}`
                    );
                  }

                  // Get the most recent follow-up date
                  const lastFollowUp =
                    so.followUps && so.followUps.length > 0
                      ? so.followUps.reduce((latest, fu) =>
                          new Date(fu.createdAt) > new Date(latest.createdAt)
                            ? fu
                            : latest
                        )
                      : null;

                  return (
                    <Table.Tr key={so.id}>
                      <Table.Td>
                        <Text fw={500}>{so.case?.caseNumber || "N/A"}</Text>
                      </Table.Td>
                      <Table.Td>
                        {so.externalInstitution?.name || "N/A"}
                      </Table.Td>
                      <Table.Td>
                        <Badge color="red" variant="filled" size="sm">
                          {ageDays} days
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        {so.shippedAt
                          ? formatSendOutInstantDate(so.shippedAt, {
                              omitYear: true,
                            })
                          : "N/A"}
                      </Table.Td>
                      <Table.Td>
                        {so.expectedReturnDate
                          ? formatExpectedReturnDate(so.expectedReturnDate, {
                              omitYear: true,
                            })
                          : "N/A"}
                      </Table.Td>
                      <Table.Td>
                        {materials.length > 0 ? materials.join(", ") : "N/A"}
                      </Table.Td>
                      <Table.Td>
                        {lastFollowUp ? (
                          <Text size="sm">
                            {formatSendOutInstantDate(lastFollowUp.createdAt, {
                              omitYear: true,
                            })}
                          </Text>
                        ) : (
                          <Text size="sm" c="dimmed">
                            None
                          </Text>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Box>

          <Divider />

          {/* Footer */}
          <Group justify="flex-end">
            <Button
              leftSection={<IconFileTypePdf size={16} />}
              variant="light"
              color="red"
              onClick={handleExportPDF}
            >
              Export PDF
            </Button>
            <Button variant="subtle" onClick={onClose}>
              Close
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
